import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import styles from "./setDefault.module.css";
import { toast } from "react-toastify";
import SubmitConfirmation from "../../AdminProducts/MarketPlaceDetail/SubmitConfirmation";

/**
 * WHO SELLS EACH SHELF
 *
 * The old screen on this tab was a tier matrix: one row per tier per vendor,
 * a checkbox in every cell, and the admin left to work out which ticks belong
 * together. After the phase-1/2 split that is the wrong shape entirely — an
 * offer holds exactly one vendor's ladder, so the decision is never
 * "which tiers" but "which VENDOR sells this shelf", once per shelf.
 *
 * So: one row per shelf (plan · type · billing period · region · currency)
 * and one compact picker naming every vendor that quotes on it with their
 * headline price, payout and margin. A vendor that does not quote is shown
 * blank rather than omitted, because "nobody else is offering this" is
 * information. A shelf with no seller is the loudest thing on the page and
 * one click from fixed.
 *
 * Everything is still enforced server-side: the save posts a shelf-shaped
 * payload to /admin/update_unit_defaults (which also still accepts the old
 * tier matrix), and `enforceDefaultSeller` guarantees at most one seller per
 * shelf and rewrites show_default in lockstep.
 */

const API = process.env.REACT_APP_SERVER_URL;

const money = (currency, value) => {
  const n = Number(value || 0);
  const sym = currency === "INR" ? "₹" : currency === "USD" ? "$" : "";
  const text = n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
  return sym ? `${sym}${text}` : `${text} ${currency || ""}`.trim();
};

const vendorKeyOf = (vendorId) =>
  vendorId === null || vendorId === undefined || vendorId === ""
    ? "null"
    : String(Number(vendorId));

const vendorIdFromKey = (key) => (key === "null" ? null : Number(key));

const shelfPeriod = (s) =>
  `${s.n_interval ?? 1} ${s.interval || ""}`.trim() || "—";

// A price (one vendor's offer) is live when its row is Active. A shelf is live
// when at least one of its offers is — otherwise nobody new can buy it and it
// only exists for the customers still renewing on it.
const offerLive = (o) => String(o?.status || "").toLowerCase() === "active";
const shelfLive = (sh) => (sh.offers || []).some(offerLive);

function SetDefault({ skuId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({}); // shelf key -> vendor key
  const [expanded, setExpanded] = useState({});
  const [query, setQuery] = useState("");
  const [view, setView] = useState("all"); // all | noseller | duplicates | retired
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [bulkVendor, setBulkVendor] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(null); // { shelf, value }

  // A shelf's identity for local state: the slot when it has one, else the
  // price row it was synthesised from (legacy, pre-backfill rows).
  const rowKey = (s) =>
    s.slot_id ? `slot:${s.slot_id}` : `price:${s.canonical_price_id}`;

  const load = useCallback(async () => {
    if (!skuId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/catalog/shelves?skuId=${skuId}`, {
        credentials: "include",
      });
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to load the shelves");
        setLoading(false);
        return;
      }
      setData(body);
      const initial = {};
      (body.shelves || []).forEach((s) => {
        initial[s.slot_id ? `slot:${s.slot_id}` : `price:${s.canonical_price_id}`] =
          s.seller_vendor_id === null || s.seller_vendor_id === undefined
            ? ""
            : vendorKeyOf(s.seller_vendor_id);
      });
      setPicked(initial);
    } catch {
      toast.error("Failed to load the shelves");
    }
    setLoading(false);
  }, [skuId]);

  useEffect(() => {
    load();
  }, [load]);

  const shelves = useMemo(() => data?.shelves || [], [data]);

  const originalPick = useCallback(
    (s) =>
      s.seller_vendor_id === null || s.seller_vendor_id === undefined
        ? ""
        : vendorKeyOf(s.seller_vendor_id),
    [],
  );

  const changed = useMemo(
    () =>
      shelves.filter(
        (s) => (picked[rowKey(s)] || "") !== originalPick(s),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shelves, picked, originalPick],
  );

  const noSellerCount = shelves.filter((s) => !s.has_seller && s.sellable_offers > 0).length;
  const unpricedCount = shelves.filter((s) => s.sellable_offers === 0).length;
  // TWO SHELVES ON ONE KEY ARE USUALLY AN OLD PRICE AND ITS REPLACEMENT: the
  // old row set Inactive and kept for the customers still renewing on it, the
  // new one Active. That is not a duplicate a buyer can see. Only a key held
  // by two or more ACTIVE shelves is — that is the clash to fix.
  // Keys held by two or more ACTIVE shelves.
  const clashKeys = useMemo(() => {
    const live = new Map();
    for (const sh of shelves) {
      if (!sh.duplicate_of_key) continue;
      if (!shelfLive(sh)) continue;
      live.set(sh.duplicate_of_key, (live.get(sh.duplicate_of_key) || 0) + 1);
    }
    return new Set([...live].filter(([, n]) => n > 1).map(([k]) => k));
  }, [shelves]);
  const isClash = (sh) => Boolean(sh.duplicate_of_key) && clashKeys.has(sh.duplicate_of_key);
  const isOlderPrice = (sh) => Boolean(sh.duplicate_of_key) && !isClash(sh) && !shelfLive(sh);
  const duplicateCount = shelves.filter(isClash).length;
  const retiredCount = shelves.filter((s) => s.status === "retired").length;
  const activeCount = shelves.filter(shelfLive).length;
  const inactiveCount = shelves.length - activeCount;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shelves.filter((s) => {
      if (view === "noseller" && !(!s.has_seller && s.sellable_offers > 0)) return false;
      if (view === "duplicates" && !(s.duplicate_of_key && clashKeys.has(s.duplicate_of_key))) return false;
      if (view === "retired" && s.status !== "retired") return false;
      if (statusFilter === "active" && !shelfLive(s)) return false;
      if (statusFilter === "inactive" && shelfLive(s)) return false;
      if (!q) return true;
      const haystack = [
        s.plan_name,
        s.type_name,
        s.interval,
        s.region,
        s.currency,
        ...(s.offers || []).map((o) => o.vendor_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [shelves, query, view, statusFilter, clashKeys]);

  /* ── the one decision this screen makes ──────────────────────────────── */

  const pick = (shelf, vendorKey) => {
    setPicked((prev) => ({ ...prev, [rowKey(shelf)]: vendorKey }));
  };

  // "Make <vendor> the seller for every shelf on this product where they
  // quote." Shelves they do not quote on are left exactly as they are — a
  // bulk action that blanked them would be a way to lose sellers by accident.
  const applyBulk = () => {
    if (!bulkVendor) return;
    const targets = shelves.filter((s) =>
      (s.offers || []).some(
        (o) => vendorKeyOf(o.vendor_id) === bulkVendor && o.tiers.length,
      ),
    );
    const hit = targets.length;
    setPicked((prev) => {
      const next = { ...prev };
      targets.forEach((s) => {
        next[rowKey(s)] = bulkVendor;
      });
      return next;
    });
    const name =
      (data?.vendors || []).find((v) => vendorKeyOf(v.vendor_id) === bulkVendor)
        ?.name || "That vendor";
    toast.info(
      hit
        ? `${name} selected on ${hit} shelf${hit === 1 ? "" : "s"} — review and save.`
        : `${name} has no priced offer on this product.`,
    );
  };

  const save = async () => {
    if (!changed.length) {
      toast.info("Nothing to save — the sellers are already as selected.");
      return;
    }
    setSaving(true);
    try {
      // Slot-backed shelves go through the shelf payload. Legacy rows that the
      // catalog-slots backfill has not placed yet have no slot to name, so they
      // still use the tier matrix — the endpoint accepts both.
      const shelfPayload = [];
      const unitPayload = [];
      changed.forEach((s) => {
        const chosen = picked[rowKey(s)];
        if (!chosen) return;
        if (s.slot_id) {
          shelfPayload.push({
            slot_id: s.slot_id,
            vendor_id: vendorIdFromKey(chosen),
          });
          return;
        }
        (s.offers || []).forEach((o) => {
          const selected = vendorKeyOf(o.vendor_id) === chosen;
          o.tiers.forEach((t) =>
            unitPayload.push({ id: t.id, show_default: selected }),
          );
        });
      });

      const calls = [];
      if (shelfPayload.length) {
        calls.push({ skuId, shelves: shelfPayload });
      }
      if (unitPayload.length) {
        calls.push({ skuId, unit_ids: unitPayload });
      }

      let touched = 0;
      for (const body of calls) {
        const res = await fetch(`${API}/admin/update_unit_defaults`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });
        const out = await res.json();
        if (out.status !== 200) {
          setSaving(false);
          toast.error(out.message || "Failed to save");
          return;
        }
        touched += (out.matrix || []).filter((m) => m.changed > 0).length;
      }

      toast.success(
        touched
          ? `Seller updated on ${changed.length} shelf${changed.length === 1 ? "" : "s"}`
          : "Saved — nothing needed changing",
      );
      await load();
      if (typeof onSaved === "function") onSaved();
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  /* ── admin-only edits to the shelf itself ────────────────────────────── */

  const submitRename = async () => {
    const shelf = renaming?.shelf;
    const name = (renaming?.value || "").trim();
    if (!shelf || !name) return;
    try {
      const res = await fetch(`${API}/admin/catalog/rename-plan`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(
          shelf.slot_id
            ? { slot_id: shelf.slot_id, plan_name: name }
            : { price_id: shelf.canonical_price_id, plan_name: name },
        ),
      });
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to rename");
        return;
      }
      toast.success(body.msg);
      setRenaming(null);
      await load();
      if (typeof onSaved === "function") onSaved();
    } catch {
      toast.error("Failed to rename");
    }
  };

  const setStatus = async (shelf, status) => {
    if (!shelf.slot_id) {
      toast.error("This plan has not been placed on a shelf yet.");
      return;
    }
    try {
      const res = await fetch(
        `${API}/admin/catalog/shelves/${shelf.slot_id}/status`,
        {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status }),
        },
      );
      const body = await res.json();
      if (!body.success) {
        toast.error(body.msg || "Failed to change the shelf");
        return;
      }
      toast.success(body.msg);
      await load();
    } catch {
      toast.error("Failed to change the shelf");
    }
  };

  /* ── render ──────────────────────────────────────────────────────────── */

  const vendors = data?.vendors || [];

  const renderPicker = (shelf) => {
    const chosen = picked[rowKey(shelf)] || "";
    return (
      <div className={styles.Picker}>
        {vendors.map((v) => {
          const vk = vendorKeyOf(v.vendor_id);
          const offer = (shelf.offers || []).find(
            (o) => vendorKeyOf(o.vendor_id) === vk,
          );
          const quoted = Boolean(offer && offer.tiers.length);
          const selected = chosen === vk;

          if (!quoted) {
            // Blank, not missing: this vendor is on the product but has not
            // quoted this shelf.
            return (
              <div
                key={vk}
                className={`${styles.Offer} ${styles.OfferBlank}`}
                title={`${v.name} does not quote this shelf`}
              >
                <span className={styles.OfferVendor}>{v.name}</span>
                <span className={styles.OfferBlankDash}>—</span>
              </div>
            );
          }

          const h = offer.headline;
          return (
            <button
              type="button"
              key={vk}
              onClick={() => pick(shelf, vk)}
              className={`${styles.Offer} ${selected ? styles.OfferSelected : ""}`}
              aria-pressed={selected}
            >
              <span className={styles.OfferVendor}>
                <span className={styles.Radio} aria-hidden="true">
                  <span className={selected ? styles.RadioDot : ""} />
                </span>
                {v.name}
                {!offerLive(offer) ? (
                  <span
                    className={styles.OfferInactive}
                    title="This vendor's price is Inactive — kept for existing subscriptions, not offered to new buyers"
                  >
                    Inactive
                  </span>
                ) : null}
              </span>
              <span className={styles.OfferPrice}>
                {money(shelf.currency, h?.marketplace_price)}
              </span>
              <span className={styles.OfferMeta}>
                pays {money(shelf.currency, h?.vendor_price)}
                {h && h.margin_rate !== null ? (
                  <span
                    className={h.margin < 0 ? styles.MarginBad : styles.Margin}
                  >
                    {" "}
                    · {money(shelf.currency, h.margin)} ({h.margin_rate}%)
                  </span>
                ) : null}
              </span>
              <span className={styles.OfferMeta}>
                {offer.tiers.length} tier{offer.tiers.length === 1 ? "" : "s"}
                {offer.subscriptions
                  ? ` · ${offer.subscriptions} sub${offer.subscriptions === 1 ? "" : "s"}`
                  : ""}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderLadder = (shelf) => {
    const chosen = picked[rowKey(shelf)] || "";
    const offers = (shelf.offers || []).filter((o) => o.tiers.length);
    if (!offers.length) {
      return (
        <div className={styles.Ladder}>
          <p className={styles.LadderEmpty}>
            Nobody has priced this shelf. It cannot be sold until a vendor's
            tiers are approved on it.
          </p>
        </div>
      );
    }
    const selectedOffer =
      offers.find((o) => vendorKeyOf(o.vendor_id) === chosen) || offers[0];
    return (
      <div className={styles.Ladder}>
        <div className={styles.LadderHead}>
          {selectedOffer.vendor_name}'s ladder — this is exactly what a buyer is
          charged when they sell this shelf
        </div>
        <table className={styles.LadderTable}>
          <thead>
            <tr>
              <th>Units</th>
              <th>Steps</th>
              <th>Buyer pays</th>
              <th>Vendor gets</th>
              <th>Margin</th>
            </tr>
          </thead>
          <tbody>
            {selectedOffer.tiers.map((t) => (
              <tr key={t.id}>
                <td>
                  {t.start_range} – {t.end_range}
                  {t.is_headline ? (
                    <span className={styles.HeadlineTag}>headline</span>
                  ) : null}
                </td>
                <td>{t.steps ?? "—"}</td>
                <td>{money(shelf.currency, t.marketplace_price)}</td>
                <td>{money(shelf.currency, t.vendor_price)}</td>
                <td className={t.margin < 0 ? styles.MarginBad : undefined}>
                  {money(shelf.currency, t.margin)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {offers.length > 1 ? (
          <p className={styles.LadderNote}>
            {offers.length - 1} other vendor
            {offers.length - 1 === 1 ? "" : "s"} quote this shelf — pick one
            above to see their ladder. Buyers are never shown a mix.
          </p>
        ) : null}
        <div className={styles.LadderActions}>
          <Button
            size="small"
            onClick={() =>
              setRenaming({ shelf, value: shelf.plan_name || "" })
            }
          >
            Rename plan
          </Button>
          {shelf.slot_id ? (
            shelf.status === "retired" ? (
              <Button size="small" onClick={() => setStatus(shelf, "active")}>
                Put back in the catalogue
              </Button>
            ) : (
              <Button
                size="small"
                color="warning"
                onClick={() => setStatus(shelf, "retired")}
              >
                Retire shelf
              </Button>
            )
          ) : null}
          <span className={styles.SlotId}>
            {shelf.slot_id ? `shelf #${shelf.slot_id}` : "not placed on a shelf yet"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.Container}>
      <div className={styles.HeaderBar}>
        <div>
          <h3 className={styles.Title}>Who sells each shelf</h3>
          <p className={styles.Subtitle}>
            One row per plan a buyer can see. Pick the vendor that sells it —
            their whole ladder is what gets charged, never a mix of vendors.
            {changed.length ? (
              <strong className={styles.Dirty}>
                {" "}
                {changed.length} unsaved change
                {changed.length === 1 ? "" : "s"}
              </strong>
            ) : null}
          </p>
        </div>
        <div className={styles.HeaderActions}>
          <Tooltip title="Reload">
            <IconButton onClick={load} disabled={loading || saving}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => setConfirming(true)}
            disabled={saving || !changed.length}
            className={styles.SaveButton}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className={styles.Toolbar}>
        <TextField
          size="small"
          placeholder="Search plan, type, region or vendor"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={styles.Search}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={styles.StatusSelect}
          aria-label="Price status"
        >
          <MenuItem value="all">All statuses ({shelves.length})</MenuItem>
          <MenuItem value="active">Active ({activeCount})</MenuItem>
          <MenuItem value="inactive">Inactive ({inactiveCount})</MenuItem>
        </Select>
        <div className={styles.Filters}>
          <Chip
            label={`All (${shelves.length})`}
            onClick={() => setView("all")}
            color={view === "all" ? "primary" : "default"}
            variant={view === "all" ? "filled" : "outlined"}
            size="small"
          />
          <Chip
            label={`No seller (${noSellerCount})`}
            onClick={() => setView("noseller")}
            color={view === "noseller" ? "error" : noSellerCount ? "error" : "default"}
            variant={view === "noseller" ? "filled" : "outlined"}
            size="small"
          />
          <Chip
            label={`Duplicates (${duplicateCount})`}
            onClick={() => setView("duplicates")}
            color={view === "duplicates" ? "warning" : "default"}
            variant={view === "duplicates" ? "filled" : "outlined"}
            size="small"
          />
          <Chip
            label={`Retired (${retiredCount})`}
            onClick={() => setView("retired")}
            color={view === "retired" ? "primary" : "default"}
            variant={view === "retired" ? "filled" : "outlined"}
            size="small"
          />
          {/* The chips above filter THIS product. Merging a duplicate away is
              a cleanup job across the whole catalogue and is rare enough that
              it does not earn a permanent place in the nav — so the way in is
              here, where the duplicates are actually visible, and only when
              this product has some. */}
          {duplicateCount > 0 && (
            <a
              href="/dashboard/catalog_duplicates"
              className={styles.DupLink}
              title="Retire or merge duplicate shelves across every product"
            >
              Clean up duplicates across all products →
            </a>
          )}
        </div>
        <div className={styles.Bulk}>
          <Select
            size="small"
            displayEmpty
            value={bulkVendor}
            onChange={(e) => setBulkVendor(e.target.value)}
            className={styles.BulkSelect}
          >
            <MenuItem value="">Choose a vendor…</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={vendorKeyOf(v.vendor_id)} value={vendorKeyOf(v.vendor_id)}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
          <Button
            size="small"
            variant="outlined"
            disabled={!bulkVendor}
            onClick={applyBulk}
          >
            Make seller everywhere they quote
          </Button>
        </div>
      </div>

      {noSellerCount ? (
        <div className={styles.Alert}>
          <strong>
            {noSellerCount} shelf{noSellerCount === 1 ? " has" : "s have"} no
            seller.
          </strong>{" "}
          A shelf with priced offers and no seller is not being sold at all.
          Pick a vendor on each and save.
          <Button size="small" onClick={() => setView("noseller")}>
            Show them
          </Button>
        </div>
      ) : null}

      {unpricedCount ? (
        <p className={styles.Muted}>
          {unpricedCount} shelf{unpricedCount === 1 ? "" : "s"} nobody has
          priced — there is nothing to pick until a vendor's tiers are approved
          on {unpricedCount === 1 ? "it" : "them"}.
        </p>
      ) : null}

      {loading ? (
        <div className={styles.Loading}>
          <CircularProgress size={26} />
        </div>
      ) : !visible.length ? (
        <p className={styles.Muted}>No shelves match.</p>
      ) : (
        <div className={styles.List}>
          {visible.map((shelf) => {
            const key = rowKey(shelf);
            const open = Boolean(expanded[key]);
            const chosen = picked[key] || "";
            const dirtyRow = chosen !== originalPick(shelf);
            const noSeller = !shelf.has_seller && shelf.sellable_offers > 0;
            return (
              <div
                key={key}
                className={`${styles.Row} ${noSeller && !chosen ? styles.RowAlert : ""} ${
                  dirtyRow ? styles.RowDirty : ""
                } ${shelf.status === "retired" ? styles.RowRetired : ""}`}
              >
                <div className={styles.RowMain}>
                  <div className={styles.PlanCell}>
                    <div className={styles.PlanName}>
                      <span>{shelf.plan_name || "(unnamed plan)"}</span>
                      <Tooltip title="Rename this plan everywhere — the shelf and every vendor's offer on it">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setRenaming({ shelf, value: shelf.plan_name || "" })
                          }
                        >
                          <EditOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <div className={styles.PlanMeta}>
                      <span className={styles.TypeBadge}>{shelf.type_name}</span>
                      <span>{shelfPeriod(shelf)}</span>
                      <span>{shelf.region || "all regions"}</span>
                      <span>{shelf.currency || "—"}</span>
                      {shelf.subscriptions ? (
                        <span className={styles.Subs}>
                          {shelf.subscriptions} live sub
                          {shelf.subscriptions === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                    <div className={styles.Badges}>
                      {noSeller ? (
                        <span className={styles.BadgeAlert}>No seller</span>
                      ) : null}
                      {shelf.sellable_offers === 0 ? (
                        <span className={styles.BadgeMuted}>Not priced</span>
                      ) : null}
                      {!shelfLive(shelf) ? (
                        <span
                          className={styles.BadgeInactive}
                          title="No Active price on this shelf — new buyers cannot buy it; existing subscriptions keep renewing on it."
                        >
                          Inactive
                        </span>
                      ) : null}
                      {isClash(shelf) ? (
                        <span className={styles.BadgeWarn}>Duplicate shelf</span>
                      ) : isOlderPrice(shelf) ? (
                        <span
                          className={styles.BadgeMuted}
                          title="An inactive older price kept for the customers still renewing on it; new buyers only see the active one."
                        >
                          Older price
                          {shelf.subscriptions ? ` · ${shelf.subscriptions} subs` : ""}
                        </span>
                      ) : null}
                      {shelf.status === "retired" ? (
                        <span className={styles.BadgeMuted}>Retired</span>
                      ) : null}
                    </div>
                  </div>

                  {renderPicker(shelf)}

                  <div className={styles.RowActions}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [key]: !p[key] }))
                      }
                      aria-label={open ? "Hide the ladder" : "Show the ladder"}
                    >
                      {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </div>
                </div>
                {open ? renderLadder(shelf) : null}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={Boolean(renaming)} onClose={() => setRenaming(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rename this plan</DialogTitle>
        <DialogContent>
          <p className={styles.DialogNote}>
            The name belongs to the shelf, so this renames it for every vendor
            selling or quoting it — buyers must never see one plan under two
            names. No price changes.
          </p>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Plan name"
            value={renaming?.value || ""}
            onChange={(e) =>
              setRenaming((p) => ({ ...p, value: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!(renaming?.value || "").trim()}
            onClick={submitRename}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {confirming && (
        <SubmitConfirmation
          handleClose={() => setConfirming(false)}
          handleSave={async () => {
            await save();
            setConfirming(false);
          }}
          submitLoader={saving}
        />
      )}
    </div>
  );
}

export default SetDefault;
